import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { NETWORK_METROS } from "../../constants/networkCities";

// Code-split: react-globe.gl + three.js together are ~400KB gzipped. They
// don't enter the bundle until this component renders.
const Globe = lazy(() => import("react-globe.gl"));

const POINTS = NETWORK_METROS
  .filter((m) => typeof m.lat === "number" && typeof m.lng === "number")
  .map((m) => ({
    name: m.name,
    lat: m.lat,
    lng: m.lng,
    count: m.count,
  }));

// Pin color — classic map-pin red.
const PIN_RED = "#ef4444";
// Softer red for the atmospheric glow + propagating rings, so the edge
// effects don't overpower the pins themselves.
const PIN_RED_SOFT = "#fca5a5";

// Build a real DOM map-pin SVG sized in screen px. Anchor the tip (bottom
// of the teardrop) on the lat/lng with translate(-50%, -100%).
const buildPinElement = (d) => {
  const el = document.createElement("div");
  el.style.cssText =
    "transform: translate(-50%, -100%); pointer-events: none; filter: drop-shadow(0 3px 5px rgba(0,0,0,0.55));";
  el.innerHTML = `
    <svg width="22" height="28" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 1C6.48 1 2 5.48 2 11c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10z"
            fill="${PIN_RED}" stroke="#ffffff" stroke-width="1.6" stroke-linejoin="round" />
      <circle cx="12" cy="11" r="3.6" fill="#ffffff" />
    </svg>
  `;
  el.title = `${d.name} · ${d.count} ${d.count === 1 ? "city" : "cities"}`;
  return el;
};

/**
 * NetworkGlobe — a 3D globe that plots every RunCards metro and slowly auto-
 * rotates. Mounted lazily after the section scrolls into view so the heavy
 * three.js bundle never blocks initial load.
 *
 * Sized by a ResizeObserver on the wrapper so it fills its aspect-ratio parent
 * cleanly at any breakpoint.
 */
export default function NetworkGlobe() {
  const wrapRef = useRef(null);
  const globeRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [inView, setInView] = useState(false);

  // Defer mount until visible.
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      // Large lead margin so the heavy three.js chunk + WebGL init begin well
      // before the section scrolls in — the globe is ready by the time it's
      // visible instead of popping in late (noticeable on slower mobile).
      { rootMargin: "1200px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Track container size for the canvas.
  useEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Wire up auto-rotate + lock interaction once the globe mounts.
  const handleGlobeReady = () => {
    const g = globeRef.current;
    if (!g) return;
    const controls = g.controls();
    if (!controls) return;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.4;
    controls.enableZoom = false;
    controls.enablePan = false;
    // Initial framing — North America forward.
    g.pointOfView({ lat: 30, lng: -60, altitude: 2.4 }, 0);
  };

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 overflow-hidden"
      // Subtle vignette so the globe edge dissolves into the panel.
      style={{
        maskImage:
          "radial-gradient(ellipse at center, black 65%, transparent 100%)",
        WebkitMaskImage:
          "radial-gradient(ellipse at center, black 65%, transparent 100%)",
      }}
    >
      {inView && size.w > 0 && size.h > 0 && (
        <Suspense fallback={null}>
          <Globe
            ref={globeRef}
            width={size.w}
            height={size.h}
            backgroundColor="rgba(0,0,0,0)"
            // NASA Blue Marble — the iconic colorful Earth. Bump image adds
            // 3D relief on the continents.
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            showAtmosphere
            atmosphereColor="#a0d8ef"
            atmosphereAltitude={0.2}
            onGlobeReady={handleGlobeReady}
            // Red map pins per metro — DOM SVGs face the camera so they
            // always read as proper pins, never sideways cylinders.
            htmlElementsData={POINTS}
            htmlLat="lat"
            htmlLng="lng"
            htmlAltitude={0.01}
            htmlElement={buildPinElement}
            // Soft red propagating halo under each pin for a subtle pulse.
            ringsData={POINTS}
            ringLat="lat"
            ringLng="lng"
            ringColor={() => (t) => `rgba(239,68,68,${0.55 * (1 - t)})`}
            ringMaxRadius={2.2}
            ringPropagationSpeed={1.4}
            ringRepeatPeriod={2400}
            // Floating labels — only the largest metros to keep it readable.
            // No labelDot (the red pin already marks the spot).
            labelsData={POINTS.filter((p) => p.count >= 3)}
            labelLat="lat"
            labelLng="lng"
            labelText="name"
            labelSize={0.55}
            labelDotRadius={0}
            labelColor={() => "rgba(255,255,255,0.9)"}
            labelResolution={2}
            labelAltitude={0.02}
          />
        </Suspense>
      )}
    </div>
  );
}
